import { useQuery, useQueries, type UseQueryResult } from '@tanstack/react-query'
import { type PaginatedResponse, type DetailResponse } from '../../../api/client'
import { type PaginationOptions } from '../../../api/pagination'
import {
  fetchContact,
  fetchContacts,
  fetchRichContacts,
  fetchAllContacts,
  fetchAllRichContacts,
  searchContacts,
  type ContactFilters,
  ContactGroupFilters,
  fetchContactGroups,
  searchContactGroups,
  fetchContactGroup,
  fetchAllContactGroups,
} from '../api/contactApi'
import {
  type PaginatedQueryParameters,
  type DetailQueryParameters,
} from '../../../api/hooks/utils/types'
import { useSmartListQuery, useSmartDetailQuery } from '../../../api/hooks/useSmartQueries'
import { contactGroupKeys, contactKeys, richContactKeys } from '../utils/queryKeys'
import { ContactGroup } from '../../../utils/types'

/**
 * Hook that provides the available ContactGroup instances that are matching the
 * specified query parameters.
 * @param parameters - The parameters that define which instances to fetch from the backend.
 * @param instances - Array of instance ids or urls to fetch from the backend.
 * @param enabled - Disabling the query will prevent it from running instantly
 *  after the parameters or instances changed.
 */
export function useContactGroups(
  parameters: PaginatedQueryParameters<ContactGroup, ContactGroupFilters>,
  instances: number[] | string[] | undefined = undefined,
  enabled = true
): UseQueryResult<PaginatedResponse<ContactGroup>> {
  return useSmartListQuery<ContactGroup, ContactGroupFilters>(
    {
      queryKeyFactory: contactGroupKeys,
      queryParameters: parameters,
      queryFn: (_, ids: number[] | undefined) => {
        // If IDs are defined, it means a search was performed (or instances were provided)
        if (ids !== undefined) {
          // If search returned no results, return empty page immediately
          if (ids.length === 0) {
            return Promise.resolve({
              results: [],
              count: 0,
              next: null,
              previous: null,
            } as PaginatedResponse<ContactGroup>)
          }
          // If search returned results, fetch them by ID without re-applying filters
          // (filters were already applied by the search)
          return fetchContactGroups({ ...parameters, filters: undefined }, ids)
        }
        // No search performed (normal list fetch), use original parameters including filters
        return fetchContactGroups(parameters, ids)
      },
      elasticSearchFn: () => searchContactGroups(parameters),
    },
    enabled,
    instances
  )
}

/**
 * Hook to fetch a single contact group by ID or URL
 */
export function useContactGroup(
  idOrUrl: number | string,
  parameters: DetailQueryParameters<ContactGroup>,
  enabled = true
): UseQueryResult<DetailResponse<ContactGroup>> {
  return useSmartDetailQuery(
    idOrUrl,
    {
      queryKeyFactory: contactGroupKeys,
      queryParameters: parameters,
      queryFn: (_, id) => fetchContactGroup(id, parameters.expand),
    },
    enabled
  )
}

/**
 * Hook to fetch all contacts.
 */
export function useAllContactGroups(
  options: Omit<PaginationOptions, 'page'> = {},
  enabled = true
): UseQueryResult<ContactGroup[], Error> {
  return useQuery({
    queryKey: [...contactGroupKeys.all, 'all', options] as const,
    queryFn: () => fetchAllContactGroups(options),
    enabled,
  })
}

/**
 * Hook to fetch all rich contacts.
 */
// export function useAllRichContacts(
//   options: Omit<PaginationOptions, 'page'> = {},
//   enabled = true
// ): UseQueryResult<RichContact[], Error> {
//   return useQuery({
//     queryKey: [...richContactKeys.all, 'all', options] as const,
//     queryFn: () => fetchAllRichContacts(options),
//     enabled,
//   })
// }

/**
 * Hook to fetch multiple contacts from Elasticsearch search results
 * Loads full contact data from the database for each contact ID
 */
// export function useContactsFromSearch(contactIds: string[], expand?: string[], enabled = true) {
//   const queries = useQueries({
//     queries: contactIds.map(id => ({
//       queryKey: contactKeys.detail(id, { expand }),
//       queryFn: () => fetchContact(id, expand),
//       enabled: enabled && !!id,
//       retry: 1,
//       staleTime: 5 * 60 * 1000, // 5 minutes
//     })),
//   })

//   // Extract contacts from query results
//   const contacts = queries
//     .filter(query => query.isSuccess && query.data)
//     .map(query => query.data as Contact)

//   // Check if any queries are still loading
//   const isLoading = queries.some(query => query.isLoading)

//   // Check if all queries are done (either success or error)
//   const isIdle = queries.every(query => query.status === 'pending')
//   const isDone = queries.every(query => query.status !== 'pending')

//   // Check for errors
//   const hasErrors = queries.some(query => query.isError)
//   const errors = queries.filter(query => query.isError).map(query => query.error)

//   // Calculate progress
//   const totalQueries = contactIds.length
//   const completedQueries = queries.filter(query => query.status !== 'pending').length
//   const progress = totalQueries > 0 ? (completedQueries / totalQueries) * 100 : 0

//   return {
//     contacts,
//     isLoading,
//     isIdle,
//     isDone,
//     hasErrors,
//     errors,
//     progress,
//     queries,
//   }
// }
